import { useShopCheck } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { imageUrl } from '@/api/client';
import type { Player } from '@/types';

interface Props {
  player: Player;
}

export function PlayerHeader({ player }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const openModal = useUIStore((s) => s.openModal);
  const { data: shopCheck } = useShopCheck(userId);
  const shopAvailable = !!shopCheck?.available;

  const xpMax = Math.max(1, player.next_level_xp ?? 1);
  const xpPercent = Math.min(100, ((player.experience ?? 0) / xpMax) * 100);
  const energyMax = Math.max(1, player.max_energy ?? 100);
  const energyPercent = Math.min(100, ((player.energy ?? 0) / energyMax) * 100);

  return (
    <div
      className="absolute top-0 inset-x-0 z-20 glass border-b border-white/5 px-3 pb-2"
      style={{
        paddingTop:
          'calc(max(env(safe-area-inset-top, 0px), var(--tg-content-safe-area-top, 0px), var(--tg-safe-area-top, 0px)) + 0.5rem)',
      }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={() => openModal('avatar')}
          className="w-14 h-14 rounded-2xl overflow-hidden ring-2 ring-white shrink-0 bg-bg-elevated shadow-card"
        >
          {player.avatar_url ? (
            <img
              src={imageUrl(player.avatar_url) ?? ''}
              alt={player.character_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xl text-white">
              {player.character_name.charAt(0).toUpperCase()}
            </div>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {player.character_name}
              </div>
              <div className="text-[10px] text-white/55 tabular-nums">
                Уровень {player.level}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <BarRow
              label="XP"
              value={`${player.experience ?? 0}/${xpMax}`}
              percent={xpPercent}
              barClass="bg-gradient-to-r from-[#007aff] to-[#00c6ff]"
            />
            <BarRow
              label="⚡"
              value={`${player.energy ?? 0}/${energyMax}`}
              percent={energyPercent}
              barClass="bg-gradient-to-r from-[#ef4444] to-[#f97316]"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1 shrink-0 bg-black/25 rounded-xl px-2 py-1.5">
          <Currency
            iconSrc="/data/interface/gold.webp"
            alt="Gold"
            value={player.currency ?? 0}
          />
          <Currency
            iconSrc="/data/interface/stardust.webp"
            alt="Stardust"
            value={player.stardust ?? 0}
          />
        </div>

        {shopAvailable && (
          <button
            onClick={() => openModal('shop')}
            className="shrink-0 w-10 h-10 rounded-xl bg-bg-elevated border border-gold/30 flex items-center justify-center active:scale-95 overflow-hidden"
            aria-label="Open shop"
          >
            <img
              src="/data/interface/shop.webp"
              alt="Shop"
              className="w-7 h-7 object-contain"
            />
          </button>
        )}
      </div>
    </div>
  );
}

function BarRow({
  label,
  value,
  percent,
  barClass,
}: {
  label: string;
  value: string;
  percent: number;
  barClass: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-4 text-[10px] text-white/70 text-center leading-none shrink-0">
        {label}
      </span>
      <div className="flex-1 h-1.5 rounded-full bg-black/35 overflow-hidden">
        <div
          className={`h-full rounded-full ${barClass} transition-[width] duration-300`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-[10px] tabular-nums text-white/55 leading-none shrink-0 min-w-[3.2rem] text-right">
        {value}
      </span>
    </div>
  );
}

function Currency({
  iconSrc,
  alt,
  value,
}: {
  iconSrc: string;
  alt: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-1">
      <img
        src={iconSrc}
        alt={alt}
        className="w-4 h-4 object-contain shrink-0"
      />
      <span className="text-[11px] font-semibold tabular-nums text-accent min-w-[2.2rem] text-right">
        {value}
      </span>
    </div>
  );
}
